import { useEffect, useMemo, useState } from 'react';
import throttle from 'lodash/throttle';
import noop from 'lodash/noop';
import { useSession } from '@sdk/session/SessionContext';

import { OnContentChangeCallback } from '@web/richText/TextEditorInner/TextEditorInner';
import {
  IncomingCommand,
  OutgoingCommand,
} from '@web/components/SocketConnectionProvider/types/command';
import { useConversationState } from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { useSocketConnectionContext } from '@web/components/SocketConnectionProvider/SocketConnectionProvider';
import { useConversationPersonas } from '@web/conversations/Conversation/ConversationProvider/hooks/useConversationPersonas/useConversationPersonas';

import { CHECK_LOOP_FREQ, THROTTLE_NOTIFY } from './conversationTypingNotifier.consts';
import { createLabel, filterOutInactiveUsers } from './conversationTypingNotifier.utils';

export const useTypingNotifier = () => {
  const { currentUser } = useSession();
  const { id } = useConversationState();
  const { send } = useSocketConnectionContext();

  return useMemo(() => {
    if (!currentUser.settings.showTyping) return noop;

    const fn: OnContentChangeCallback = ({ text }) => {
      const textValue = text.trim() ?? '';

      if (!textValue.length) return;

      send({
        command: OutgoingCommand.BROADCAST,
        room: `conversation:${id}`,
        data: {
          ignore: currentUser.id,
          command: OutgoingCommand.CONVERSATION_TYPING,
          msg: {
            id: currentUser.id,
            conversationId: id,
          },
        },
      });
    };

    return throttle(fn, THROTTLE_NOTIFY, {
      leading: true,
      trailing: false,
    });
  }, [currentUser, id, send]);
};

export const useTypingObserver = () => {
  const { subscribe } = useSocketConnectionContext();
  const personas = useConversationPersonas();
  const { currentUser } = useSession();
  const { id } = useConversationState();
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    // user ID -> last typing timestamp
    const typingUsers = new Map<ID, number>();
    let timeoutId = null;

    const checkActivity = () => {
      clearTimeout(timeoutId);

      const shouldTrackActivity = !!typingUsers.size;
      const label = createLabel(typingUsers, personas);
      setLabel(label);

      if (!shouldTrackActivity) return;

      timeoutId = setTimeout(() => {
        filterOutInactiveUsers(typingUsers);
        checkActivity();
      }, CHECK_LOOP_FREQ);
    };

    const unsubscribeConversationTypingListener = subscribe(
      IncomingCommand.CONVERSATION_TYPING,
      ({ ignore, msg: data }) => {
        // typing event is broadcasted to every client including the sender
        // sender's ID is passed under ignore prop
        if (currentUser.id === ignore || id !== data.conversationId) return;

        typingUsers.set(data.id, Date.now());
        checkActivity();
      }
    );

    const unsubscribeMessageCreateListener = subscribe(
      IncomingCommand.MESSAGE_CREATE,
      ({ msg: data }) => {
        typingUsers.delete(data.author.id);
        checkActivity();
      }
    );

    return () => {
      setLabel(null);
      clearTimeout(timeoutId);
      unsubscribeConversationTypingListener();
      unsubscribeMessageCreateListener();
    };
  }, [currentUser, personas, subscribe, id]);

  return label;
};
